import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { Form as CustomForm } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  title: Yup.string().when('type', {
    is: GraphQLTypes.LinkInfoType.OTHER,
    then: Yup.string().required('Please provide a name for the link').trim(),
  }),
  url: Yup.string()
    .required('Please provide a URL.')
    .url('Please provide a valid URL.'),
  type: Yup.string().required('Please select a link type'),
});

export const AddHomepageLinkModal = ({
  isOpen,
  onClose,
  graphRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphRef: GraphRef;
}) => {
  const [addLink, { loading, error }] = useMutation<
    GraphQLTypes.AddHomepageLinkMutation,
    GraphQLTypes.AddHomepageLinkMutationVariables
  >(
    gql`
      mutation AddHomepageLinkMutation(
        $linkType: LinkInfoType!
        $linkUrl: String!
        $linkTitle: String
        $graphVariant: String!
        $graphId: ID!
      ) {
        service(id: $graphId) {
          variant(name: $graphVariant) {
            id
            addLinkToVariant(
              type: $linkType
              url: $linkUrl
              title: $linkTitle
            ) {
              id
              links {
                type
                url
                title
                id
              }
            }
          }
        }
      }
    `,
    {
      onCompleted: () => onClose(),
    },
  );

  const { errors, values, handleChange, handleSubmit, isSubmitting, touched } =
    useFormik<Yup.InferType<typeof validationSchema>>({
      enableReinitialize: true,
      initialValues: {
        title: '',
        type: GraphQLTypes.LinkInfoType.DEVELOPER_PORTAL,
        url: '',
      },
      async onSubmit(formValues) {
        await addLink({
          variables: {
            linkType:
              formValues.type in GraphQLTypes.LinkInfoType
                ? (formValues.type as GraphQLTypes.LinkInfoType)
                : GraphQLTypes.LinkInfoType.OTHER,
            linkUrl: formValues.url,
            linkTitle: formValues.title.length ? formValues.title : null,
            graphId: graphRef.graphId,
            graphVariant: graphRef.graphVariant,
          },
        });
      },
      validationSchema,
      validateOnBlur: false,
      validateOnChange: false,
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <CustomForm onSubmit={handleSubmit}>
          <ModalHeader>
            <ModalTitle>Add a new link</ModalTitle>
            <ModalDescription>
              Add a link to your organization’s developer portal, repository, or
              other resource for your team to reference outside of GraphOS
              Studio.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {error && <ErrorMessage error={error} />}
            <FormControl
              id="add-homepage-link-modal_type"
              isRequired
              isInvalid={!!errors.type && !!touched.type}
            >
              <FormLabel>Link type</FormLabel>
              <Select
                className="w-full"
                name="type"
                onChange={handleChange}
                isDisabled={isSubmitting || loading}
              >
                <option value={GraphQLTypes.LinkInfoType.DEVELOPER_PORTAL}>
                  Developer portal
                </option>
                <option value={GraphQLTypes.LinkInfoType.REPOSITORY}>
                  Repository
                </option>
                <option value={GraphQLTypes.LinkInfoType.OTHER}>
                  Other Link
                </option>
              </Select>
              <FormErrorMessage>{errors.type}</FormErrorMessage>
            </FormControl>
            <FormControl
              id="add-homepage-link-modal_url"
              isRequired
              isInvalid={!!errors.url && !!touched.url}
            >
              <FormLabel>Url</FormLabel>
              <Input
                placeholder="https://www.linkurl.com"
                type="text"
                name="url"
                value={values.url}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.url}</FormErrorMessage>
            </FormControl>
            {values.type === GraphQLTypes.LinkInfoType.OTHER && (
              <FormControl
                id="add-homepage-link-modal_title"
                isRequired
                isInvalid={!!errors.title && !!touched.title}
              >
                <FormLabel>Title</FormLabel>
                <Input
                  placeholder="Staging site, analytics dashboard, etc"
                  type="text"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.title}</FormErrorMessage>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={isSubmitting || loading}
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting || loading}
                loadingText="Add link"
                type="submit"
              >
                Add link
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </CustomForm>
      </ModalContent>
    </Modal>
  );
};
